/**
 * A prévia real da saída no painel da mesa (E171.F3, ADR-0122 §2.7; E202.F3: saiu do editor antigo): o PDF guardado pela preparação
 * do pacote, ou "Prévia em preparação" enquanto o pacote refaz a saída depois de uma versão salva.
 * A tela não compõe nada: mostra o arquivo que o servidor guardou.
 */
import type { AxiosInstance } from 'axios';
import { useEffect, useState } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import type { SaidaDaEdicao } from '@/lib/correcaoDoRascunho';
import { baixarPreviaDoPacote, type LinhaDoPacoteData } from '@/lib/preparacao_do_pacote';

const t = ptBR.editorManual;

export interface PreviaDaSaidaProps {
  client: AxiosInstance;
  caseId: string;
  saida: SaidaDaEdicao;
  /** A linha da saída no pacote; `undefined` enquanto o pacote não foi lido. */
  linha: LinhaDoPacoteData | undefined;
  /** O pacote está preparando alguma saída (a versão salva ainda não virou prévia). */
  preparando: boolean;
  /** E202.F1: no painel da mesa, a aba já diz o que é, e o aviso da preparação é o da mesa. */
  semTitulo?: boolean;
  avisoDePreparo?: string;
}

type Situacao = 'carregando' | 'preparando' | 'indisponivel' | 'pronta';

function situacaoDa(linha: LinhaDoPacoteData | undefined, preparando: boolean): Situacao {
  if (linha === undefined) return 'carregando';
  // A saída sendo refeita (a versão salva ainda não virou prévia) não mostra o arquivo antigo.
  if (linha.estado === 'em_andamento' || linha.estado === 'aguardando') return 'preparando';
  // E170.F2: o arquivo gerado se consulta também bloqueado ou sem conferência.
  if (linha.artefato_id != null) return 'pronta';
  return preparando ? 'preparando' : 'indisponivel';
}

/** O PDF da prévia como endereço local, refeito quando o arquivo guardado muda. */
function useArquivoDaPrevia(
  client: AxiosInstance,
  caseId: string,
  saida: SaidaDaEdicao,
  artefatoId: string | null,
) {
  const [url, setUrl] = useState<string | null>(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    setUrl(null);
    setFalhou(false);
    if (artefatoId === null) return undefined;
    let vivo = true;
    let criada: string | null = null;
    baixarPreviaDoPacote(client, caseId, saida)
      .then((arquivo) => {
        if (!vivo) return;
        criada = URL.createObjectURL(arquivo.blob);
        setUrl(criada);
      })
      .catch(() => {
        if (vivo) setFalhou(true);
      });
    return () => {
      vivo = false;
      if (criada !== null) URL.revokeObjectURL(criada);
    };
  }, [client, caseId, saida, artefatoId]);

  return { url, falhou };
}

function Aviso({ texto, testId }: { texto: string; testId: string }) {
  return (
    <p
      className="border border-border bg-surface-2 p-sc-4 text-body-sm text-fg-muted"
      data-testid={testId}
    >
      {texto}
    </p>
  );
}

/** O aviso de cada situação sem arquivo à vista; `null` quando o PDF aparece. */
function avisoDa(
  situacao: Situacao,
  falhou: boolean,
  avisoDePreparo: string,
): { texto: string; testId: string } | null {
  if (situacao === 'carregando') return { texto: t.carregando, testId: 'previa-carregando' };
  if (situacao === 'preparando') return { texto: avisoDePreparo, testId: 'previa-preparando' };
  if (situacao === 'indisponivel') {
    return { texto: t.previa.indisponivel, testId: 'previa-indisponivel' };
  }
  return falhou ? { texto: t.previa.falhou, testId: 'previa-falhou' } : null;
}

export function PreviaDaSaida({
  client,
  caseId,
  saida,
  linha,
  preparando,
  semTitulo = false,
  avisoDePreparo = t.previa.preparando,
}: PreviaDaSaidaProps) {
  const situacao = situacaoDa(linha, preparando);
  const artefatoId = situacao === 'pronta' ? (linha?.artefato_id ?? null) : null;
  const { url, falhou } = useArquivoDaPrevia(client, caseId, saida, artefatoId);
  const rotulo = t.previa.rotulo.replace('{saida}', t.saida[saida]);
  const aviso = avisoDa(situacao, falhou, avisoDePreparo);

  return (
    <section
      aria-label={rotulo}
      className="space-y-sc-2 lg:sticky lg:top-sc-4 lg:self-start"
      data-testid="previa-da-saida"
    >
      {semTitulo ? null : <h2 className="text-body-lg font-semibold text-fg">{t.previa.titulo}</h2>}
      {aviso !== null ? <Aviso texto={aviso.texto} testId={aviso.testId} /> : null}
      {situacao === 'pronta' && url !== null ? (
        <iframe
          title={rotulo}
          src={url}
          className="h-[75vh] w-full border border-border bg-bg"
          data-testid="previa-pdf"
        />
      ) : null}
    </section>
  );
}
